/**
 * Where this account receives DMs: its NIP-17 kind 10050 list.
 *
 * A sender looks the list up on public relays and writes there, and the mail
 * relay is closed to reads, so the list is published to the public relays and
 * names the mail relay. Relays the account already listed are kept.
 */
import { SimplePool, type Event as NostrEvent } from "nostr-tools";
import type { MailSigner } from "./signer";
import { PROFILE_RELAYS } from "./profile";

export const KIND_DM_RELAYS = 10050;

/** The tags a new list should carry, or null when the current one already names ours. */
export function withOurRelay(current: NostrEvent | null, ours: string): string[][] | null {
  const tags = (current?.tags ?? []).filter((t) => t[0] === "relay" && t[1]);
  if (tags.some((t) => sameRelay(t[1]!, ours))) return null;
  return [["relay", ours], ...tags];
}

function sameRelay(a: string, b: string): boolean {
  const norm = (url: string) => url.trim().toLowerCase().replace(/\/+$/, "");
  return norm(a) === norm(b);
}

/**
 * Make sure the account's DM list names the mail relay.
 *
 * One signature, once per account: an unchanged list is never re-signed, which
 * on a phone signer is an approval saved on every load.
 */
export async function ensureDmRelays(
  signer: MailSigner,
  ours: string,
  relays: string[] = PROFILE_RELAYS,
  maxWait = 4000,
): Promise<boolean> {
  const pool = new SimplePool();
  try {
    // An empty answer from relays that never connected is not "no list", and
    // publishing on it would replace whatever list the account already has.
    const reachable = (await Promise.allSettled(relays.map((url) => pool.ensureRelay(url))))
      .flatMap((r, i) => (r.status === "fulfilled" ? [relays[i]!] : []));
    if (reachable.length === 0) throw new Error("no public relay reachable");
    const events = await pool.querySync(
      reachable, { kinds: [KIND_DM_RELAYS], authors: [signer.pubkey] }, { maxWait },
    );
    const newest = events.sort((a, b) => b.created_at - a.created_at)[0] ?? null;
    const tags = withOurRelay(newest, ours);
    if (!tags) return false;
    const event = await signer.signEvent({
      kind: KIND_DM_RELAYS,
      created_at: Math.floor(Date.now() / 1000),
      tags,
      content: "",
    });
    await Promise.any(pool.publish(reachable, event));
    return true;
  } finally {
    pool.close(relays);
  }
}
